// React
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Libraries
import { useTranslation } from "react-i18next";

// Components
import {
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ButtonGroup,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";

// Icons
import {
  Face,
  Logout,
  LightMode,
  DarkMode,
  SettingsBrightness,
} from "@mui/icons-material";

// Utils
import { isValidMode, stringAvatar } from "Utils/ColourUtils";

// Constants
import { MODES } from "Constants/theme";
import { useMBSAuth } from "Auth/MBS/MBSAuthContext";
import { setPendingAuthMessage } from "Auth/MBS/mbsStorage";

// Styles
import { styles } from "./styles";

export const UserMenu = ({
  username,
  modePreference,
  onModePreferenceChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useMBSAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedMode = isValidMode(modePreference)
    ? modePreference
    : MODES.LIGHT;

  const open = Boolean(anchorEl);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleModeChange = (newMode) => {
    onModePreferenceChange?.(newMode);
  };

  const handleLogout = async () => {
    setPendingAuthMessage(t("mbsAuth.feedback.signedOut"), "success");
    await signOut();
    handleClose();
    navigate("/mbs/login", { replace: true });
  };

  return (
    <>
      <Tooltip title={t("mbsNavBar.userMenu.openSettings")}>
        <IconButton
          onClick={handleOpen}
          aria-haspopup="menu"
          aria-label={t("mbsNavBar.userMenu.openSettings")}
        >
          <Avatar {...stringAvatar(username)} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem disableRipple sx={styles.menuItemNoHover}>
          <ListItemIcon>
            <Face />
          </ListItemIcon>
          <ListItemText>{username}</ListItemText>
        </MenuItem>
        <MenuItem disableRipple sx={styles.menuItemNoHover}>
          <ButtonGroup sx={styles.modeButtonGroup}>
            <Button
              variant={selectedMode === MODES.LIGHT ? "contained" : "outlined"}
              startIcon={<LightMode />}
              onClick={() => handleModeChange(MODES.LIGHT)}
            >
              {t("mbsNavBar.userMenu.light")}
            </Button>
            <Button
              variant={selectedMode === MODES.SYSTEM ? "contained" : "outlined"}
              startIcon={<SettingsBrightness />}
              onClick={() => handleModeChange(MODES.SYSTEM)}
            >
              {t("mbsNavBar.userMenu.system")}
            </Button>
            <Button
              variant={selectedMode === MODES.DARK ? "contained" : "outlined"}
              startIcon={<DarkMode />}
              onClick={() => handleModeChange(MODES.DARK)}
            >
              {t("mbsNavBar.userMenu.dark")}
            </Button>
          </ButtonGroup>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>{t("mbsNavBar.userMenu.logout")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
